// 抽奖组件逻辑 - useLottery.ts
// 管理抽奖状态和相关操作

import { ref, reactive, Ref } from 'vue';
import { getJSON } from '@/api/request';
import { getActivityApi, getUserJsonApi, getLoginUrlApi } from '@/utils';
import useGetAward, { virtualAwardDetailPopupState } from '@/hooks/useGetAward';
import { taskText } from '@/config';
import {
  mockActivityBasic,
  mockUserInfo,
  mockDelay,
} from '@/api/mock';

// 类型定义
interface V3PopupState {
  lotterySuccPopupState: boolean;
  bagPopupState: boolean;
  poolPopupState: boolean; // 奖池信息弹窗
  scoreDetailPopupState: boolean;
}

interface LotteryAndTaskState {
  totalScore: number;
  isEnd: boolean; // 活动是否已结束（activityIsEnd）
  activityIsStart: number; // 活动（任务期）是否已开始：1=已开始, 0=未开始
  activityIsEnd: number; // 活动（任务期）是否已结束：1=已结束, 0=未结束
  awardIsStart: number; // 领奖时间是否已开始：1=已开始, 0=未开始
  awardIsEnd: number; // 领奖时间是否已结束：1=已结束, 0=未结束
  canClaimVirtualReward: boolean;
  canClaimMaterialReward: boolean;
  historyScore: number;
  titlePopup?: {
    title: string;
    color: string;
  };
  // 任务状态 - 动态添加每个任务的 state 和 progress
  [key: string]: any;
}

interface LotteryConfig {
  platformId: string;
  activityName: string;
  gameId: number;
  enableMock?: boolean; // 是否启用 Mock 模式
  onBasicDataLoaded?: (data: any) => void; // basic 数据加载完成后的回调
}

export function useLottery(config: LotteryConfig) {
  const { platformId, activityName, gameId, enableMock = false, onBasicDataLoaded } = config;

  // 初始化全局变量
  window.platform_id = platformId;
  window.activity_name = activityName;
  window.game_id = gameId;
  window.activityDataLayer = window.activityDataLayer || [];
  window.activityDataLayer.push = window.activityDataLayer.push || function() {
    console.log('Analytics:', arguments);
  };

  // Mock 模式提示
  if (enableMock) {
    console.log('🎭 Mock 模式已启用 - 所有接口数据都是模拟的');
  }

  // ========== 状态管理 ==========
  const isReady = ref(false);
  const isLogin = ref(false);
  const accountName = ref('');
  const isBind = ref(false);
  const roleName = ref('');
  const sname = ref('');
  const isReserved = ref(false); // 是否已预约
  const serverTimestamp = ref<number>(0); // 服务器时间戳（秒级）

  // 邀请码相关
  const inviteCode = ref(''); // 从URL获取的邀请码（别人的邀请码）
  const myCode = ref(''); // 当前玩家自己的邀请码
  const inviteRoleName = ref(''); // 邀请人角色名
  const membersCount = ref(0); // 队伍人数

  // 弹窗状态
  const v3PopupState = reactive<V3PopupState>({
    lotterySuccPopupState: false,
    bagPopupState: false,
    poolPopupState: false, // 奖池信息弹窗
    scoreDetailPopupState: false,
  });

  // 抽奖结果
  const lotteryResult = ref<any[]>([]);

  // 抽奖和任务状态
  const lotteryAndTaskState = reactive<LotteryAndTaskState>({
    totalScore: 0, // 默认积分为0
    isEnd: false,
    activityIsStart: 1, // 默认活动已开始
    activityIsEnd: 0, // 默认活动未结束
    awardIsStart: 0, // 默认领奖期未开始
    awardIsEnd: 0, // 默认领奖期未结束
    canClaimVirtualReward: false,
    canClaimMaterialReward: false,
    historyScore: 0, // 历史总积分（用于称号计算）
    titlePopup: {
      title: '',
      color: '',
    },
    bindInfo: null, // 保存完整的 bindInfo 数据
  });

  // 任务列表（响应式）
  const taskList = ref(taskText.map(task => ({
    ...task,
    status: 0, // 任务状态：0未完成, 1已完成, 2已领取
    taskKey: '', // 用于领取任务的key
  })));

  // ========== 提示函数 ==========
  const showCurErrorTips = (code: number) => {
    const errorMessages: Record<number, string> = {
      10001: '未登录或登录已过期',
      10002: '积分不足',
      10003: '抽奖次数已用完',
      10004: '活动未开始或已结束',
      21018: '積分不足',
      21019: '抽獎失敗',
      21023: '無可領取的實物獎勵',
    };
    const message = errorMessages[code] || `错误代码: ${code}`;
    // 只有当 code 存在时才添加错误码前缀
    alert(code ? `【${code}】${message}` : message);
  };

  const showTipMessage = (msg: string) => {
    alert(msg);
  };

  // ========== 获取领奖hook ==========
  const { physicalAwardPopupState } = useGetAward(showTipMessage, showCurErrorTips);

  // ========== 核心功能函数 ==========

  /**
   * 检查登录状态
   */
  const checkLogin = (): boolean => {
    if (!isLogin.value) {
      gotoLogin();
      return false;
    }
    return true;
  };

  /**
   * 跳转登录页
   */
  const gotoLogin = () => {
    window.location.href = getLoginUrlApi(
      window.platform_id,
      window.game_id,
      'zh-TW'
    );
  };

  /**
   * 初始化活动数据
   */
  const initActivity = async () => {
    try {
      let res;

      if (enableMock) {
        // Mock 模式
        await mockDelay(300);
        res = mockActivityBasic();
      } else {
        // 真实接口
        res = await getJSON(
          getActivityApi(window.platform_id, window.activity_name, 'basic'),
          {}
        );
      }

      if (res.result === 1) {
        const data = res.data;

        // 使用 availablePoints 作为当前积分
        lotteryAndTaskState.totalScore = data?.availablePoints || 0;

        // 活动状态
        lotteryAndTaskState.activityIsStart = data?.activityIsStart ?? 1;
        lotteryAndTaskState.activityIsEnd = data?.activityIsEnd ?? 0;
        lotteryAndTaskState.awardIsStart = data?.awardIsStart ?? 0;
        lotteryAndTaskState.awardIsEnd = data?.awardIsEnd ?? 0;

        // 使用 activityIsEnd 判断活动是否结束（保持向后兼容）
        lotteryAndTaskState.isEnd = data?.activityIsEnd === 1;

        // TODO: 根据实际接口调整
        lotteryAndTaskState.canClaimVirtualReward = data?.canClaimVirtualReward || false;
        lotteryAndTaskState.canClaimMaterialReward = data?.canClaimMaterialReward || false;

        // 预约状态：reserveStatus (0: 未预约, 1: 已预约)
        isReserved.value = data?.reserveStatus === 1;

        // 队伍邀请码：teamCode 作为 myCode
        myCode.value = data?.teamCode || '';

        // 队伍人数：membersCount
        membersCount.value = data?.membersCount || 0;

        // 处理绑定信息：bindInfo 有内容且不是空数组时表示已绑定
        const bindInfo = data?.bindInfo;
        if (bindInfo && typeof bindInfo === 'object' && !Array.isArray(bindInfo)) {
          // bindInfo 是对象且有内容
          if (bindInfo.roleName && bindInfo.serverName) {
            isBind.value = true;
            const decodedRoleName = decodeURIComponent(decodeURIComponent(bindInfo.roleName || ''));
            const decodedServerName = decodeURIComponent(decodeURIComponent(bindInfo.serverName || ''));
            roleName.value = decodedRoleName;
            sname.value = decodedServerName;

            // 保存完整的 bindInfo 到 state（用于切换角色时回显）
            lotteryAndTaskState.bindInfo = {
              serverId: bindInfo.serverId, // 对应 select 的 v-model
              serverName: decodedServerName, // 使用解码后的名称
              sId: bindInfo.sId, // 另一个服务器 ID
              roleId: bindInfo.roleId,
              roleName: decodedRoleName, // 使用解码后的名称
            };
          } else {
            isBind.value = false;
            roleName.value = '';
            sname.value = '';
            lotteryAndTaskState.bindInfo = null;
          }
        } else if (Array.isArray(bindInfo) && bindInfo.length === 0) {
          // bindInfo 是空数组，表示未绑定
          isBind.value = false;
          roleName.value = '';
          sname.value = '';
          lotteryAndTaskState.bindInfo = null;
        } else {
          // bindInfo 是 undefined 或其他情况，表示未绑定
          isBind.value = false;
          roleName.value = '';
          sname.value = '';
          lotteryAndTaskState.bindInfo = null;
        }

        // 存储服务器时间戳
        if (data?.timestamp) {
          serverTimestamp.value = data.timestamp;
        }

        // 调用外部传入的回调（如果有teamCode，App.vue会调用initTeamInfo）
        if (onBasicDataLoaded && typeof onBasicDataLoaded === 'function') {
          onBasicDataLoaded(data);
        }

        // 调用 refreshTaskKey 获取任务状态
        await refreshTaskStatus();
      }
    } catch (error) {
      console.error('获取活动数据失败:', error);
    }
  };

  /**
   * 刷新任务状态
   */
  const refreshTaskStatus = async () => {
    // 判断是否满足请求条件：登录 && 绑定角色 && 已预约
    if (!isLogin.value) {
      console.log('未登录，跳过 refreshTaskCenter 请求');
      return;
    }

    if (!isBind.value) {
      console.log('未绑定角色，跳过 refreshTaskCenter 请求');
      return;
    }

    if (!isReserved.value) {
      console.log('未预约，跳过 refreshTaskCenter 请求');
      return;
    }

    try {
      let res;

      if (enableMock) {
        // Mock 模式
        await mockDelay(200);
        // TODO: 添加 mock 任务数据
        res = { result: 1, data: { taskList: [] } };
      } else {
        // 真实接口
        res = await getJSON(
          getActivityApi(window.platform_id, window.activity_name, 'refreshTaskCenter'),
          {}
        );
      }

      if (res.result === 1 && res.data?.taskList) {
        const apiTaskList = res.data.taskList;

        // 遍历返回的任务列表，更新 taskList
        apiTaskList.forEach(apiTask => {
          const taskId = apiTask.taskId;
          const subTaskList = apiTask.subTaskList || [];

          if (subTaskList.length > 0) {
            const levelConfigList = subTaskList[0]?.levelConfigList || [];

            // 处理 task_team_rally（有多个level）
            if (taskId === 'task_team_rally') {
              // 找到组队任务
              const teamTask = taskList.value.find(t => t.key === taskId);
              if (teamTask && teamTask.children) {
                // 遍历接口返回的 levelConfigList，更新每个子任务的状态
                levelConfigList.forEach(levelConfig => {
                  const levelId = levelConfig.levelId;
                  const status = parseInt(levelConfig.status) || 0;
                  const taskKeyValue = levelConfig.taskKey || '';

                  // 在 children 数组中找到对应的子任务并更新状态
                  const child = teamTask.children.find(c => c.levelId === levelId);
                  if (child) {
                    child.status = status;
                    child.taskKey = taskKeyValue;
                  }

                  // 同时保存到 lotteryAndTaskState（向后兼容）
                  lotteryAndTaskState[`${levelId}_status`] = status;
                  lotteryAndTaskState[`${levelId}_taskKey`] = taskKeyValue;
                });
              }
            } else {
              // 其他任务（只有一个level）
              if (levelConfigList.length > 0) {
                const levelConfig = levelConfigList[0];
                const status = parseInt(levelConfig.status) || 0;
                const taskKeyValue = levelConfig.taskKey || '';

                // 更新 taskList 中对应任务的状态
                const task = taskList.value.find(t => t.key === taskId);
                if (task) {
                  task.status = status;
                  task.taskKey = taskKeyValue;
                }
              }
            }
          }
        });

        console.log('✅ 任务状态已刷新:', taskList.value);
      }
    } catch (error) {
      console.error('❌ 刷新任务状态失败:', error);
    }
  };

  /**
   * 初始化 - 检查登录态并获取活动数据
   */
  const init = async () => {
    try {
      let loginRes;

      if (enableMock) {
        // Mock 模式 - 默认已登录
        await mockDelay(200);
        loginRes = mockUserInfo(true);
      } else {
        // 真实接口
        loginRes = await getJSON(getUserJsonApi(window.platform_id));
      }

      if (loginRes && +loginRes.result === 1) {
        isLogin.value = true;
        accountName.value = loginRes?.data?.LOGIN_ACCOUNT || '';
        // 注意：角色绑定信息现在从 basic 接口的 bindInfo 获取，不再从这里获取
      }
    } catch (error) {
      console.log('未登录或登录态已过期');
    }

    // 调用 initActivity 获取活动数据（包括 bindInfo）
    await initActivity();
    isReady.value = true;
  };

  // ========== 返回值 ==========
  return {
    // 状态
    isReady,
    isLogin,
    accountName,
    isBind,
    roleName,
    sname,
    isReserved,
    inviteCode,
    myCode,
    inviteRoleName,
    membersCount, // 队伍人数
    serverTimestamp, // 服务器时间戳
    v3PopupState,
    lotteryResult,
    lotteryAndTaskState,
    taskList, // 任务列表
    physicalAwardPopupState,
    virtualAwardDetailPopupState,

    // 配置
    enableMock,

    // 方法
    showCurErrorTips,
    showTipMessage,
    checkLogin,
    gotoLogin,
    initActivity,
    refreshTaskStatus,
    init,
  };
}
